// src/components/settings/shared/InteractionsSection.jsx - ENHANCED
import React, { useState } from 'react';
import { 
  Heart, MessageCircle, Share2, User, Star, Bell, Zap,
  Users, CreditCard, Calendar, ClipboardList, HandHeart
} from 'lucide-react';
import { FormField, TextInput, TextArea, SelectInput, Toggle, TabGroup } from './FormComponents';
import { INTERACTION_TYPES } from '../../configs/InteractionTypes';

export const InteractionsSection = ({ formData, onInputChange, templateType }) => {
  const [activeInteractionTab, setActiveInteractionTab] = useState('engagement');
  
  // Get available business interactions for this template type
  const availableBusinessInteractions = INTERACTION_TYPES[templateType] || {};
  const enabledBusinessInteractions = Object.keys(formData.businessInteractions || {}).filter(
    key => formData.businessInteractions[key]?.enabled
  );

  const interactionTabs = [
    { id: 'engagement', label: 'Social & Engagement' },
    { id: 'business', label: 'Business Interactions' },
    { id: 'notifications', label: 'Notifications' }
  ];

  const commentModerationOptions = [
    { value: 'none', label: 'No Moderation' },
    { value: 'manual', label: 'Manual Approval' },
    { value: 'auto', label: 'Auto-moderate with AI' },
    { value: 'whitelist', label: 'Approved Users Only' }
  ];

  const notificationFrequencies = [
    { value: 'instant', label: 'Instant' },
    { value: 'hourly', label: 'Hourly Digest' },
    { value: 'daily', label: 'Daily Digest' },
    { value: 'weekly', label: 'Weekly Digest' },
    { value: 'never', label: 'Never' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Interactions & Engagement</h3>
        <p className="text-gray-600">
          Configure how users can interact and engage with your {templateType}
        </p>
      </div>

      {/* Sub-navigation */}
      <TabGroup 
        tabs={interactionTabs}
        activeTab={activeInteractionTab}
        onChange={setActiveInteractionTab}
      />

      {/* ENGAGEMENT TAB - Existing functionality enhanced */}
      {activeInteractionTab === 'engagement' && (
        <div className="space-y-8">
          {/* Comments & Reviews */}
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-blue-600" />
              <h4 className="text-lg font-semibold text-gray-900">Comments & Reviews</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Toggle
                checked={formData.allowComments}
                onChange={(value) => onInputChange('allowComments', value)}
                label="Enable Comments"
                description="Allow visitors to leave comments"
              />

              <Toggle
                checked={formData.allowReviews}
                onChange={(value) => onInputChange('allowReviews', value)}
                label="Enable Reviews"
                description="Allow star ratings and reviews"
              />
            </div>

            {formData.allowComments && (
              <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                <FormField label="Comment Moderation">
                  <SelectInput
                    value={formData.commentModeration}
                    onChange={(value) => onInputChange('commentModeration', value)}
                    options={commentModerationOptions}
                  />
                </FormField>

                <Toggle
                  checked={formData.allowAnonymousComments}
                  onChange={(value) => onInputChange('allowAnonymousComments', value)}
                  label="Allow Anonymous Comments"
                  description="Let users comment without signing in"
                />
              </div>
            )}
          </div>

          {/* Social Features */}
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-red-600" />
              <h4 className="text-lg font-semibold text-gray-900">Social Features</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Toggle
                checked={formData.allowLikes}
                onChange={(value) => onInputChange('allowLikes', value)}
                label="Enable Likes"
                description="Let users like content"
              />

              <Toggle
                checked={formData.allowBookmarks}
                onChange={(value) => onInputChange('allowBookmarks', value)}
                label="Enable Bookmarks"
                description="Let users save content for later"
              />

              <Toggle
                checked={formData.allowSharing}
                onChange={(value) => onInputChange('allowSharing', value)}
                label="Enable Sharing"
                description="Add social media share buttons"
              />

              <Toggle
                checked={formData.showSocialCounts}
                onChange={(value) => onInputChange('showSocialCounts', value)}
                label="Show Interaction Counts"
                description="Display like, comment, and share counts"
              />
            </div>
          </div>

          {/* Contact Forms */}
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-green-600" />
              <h4 className="text-lg font-semibold text-gray-900">Contact & Basic Forms</h4>
            </div>
            
            <Toggle
              checked={formData.enableContactForm}
              onChange={(value) => onInputChange('enableContactForm', value)}
              label="Enable Contact Form"
              description="Add a basic contact form"
            />

            {formData.enableContactForm && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Contact Email">
                    <TextInput
                      type="email"
                      value={formData.contactEmail}
                      onChange={(value) => onInputChange('contactEmail', value)}
                      placeholder="contact@yourdomain.com"
                    />
                  </FormField>

                  <Toggle
                    checked={formData.requireContactName}
                    onChange={(value) => onInputChange('requireContactName', value)}
                    label="Require Name"
                    description="Name field is required"
                  />
                </div>

                <FormField label="Auto-response Message">
                  <TextArea
                    value={formData.autoResponseMessage}
                    onChange={(value) => onInputChange('autoResponseMessage', value)}
                    placeholder="Thank you for contacting us. We'll get back to you soon!"
                    rows={3}
                  />
                </FormField>
              </div>
            )}
          </div>
        </div>
      )}

      {/* BUSINESS INTERACTIONS TAB - New functionality */}
      {activeInteractionTab === 'business' && (
        <div className="space-y-8">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Business & Transactional Features</h4>
            <p className="text-gray-600">
              Enable advanced interactions like applications, bookings, and transactions
            </p>
          </div>

          {/* Business Interactions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(availableBusinessInteractions).map(([key, config]) => {
              const isEnabled = formData.businessInteractions?.[key]?.enabled;
              
              return (
                <div 
                  key={key} 
                  className={`border rounded-xl p-6 transition-all ${
                    isEnabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <config.icon className={`h-8 w-8 ${isEnabled ? 'text-blue-600' : 'text-gray-400'}`} />
                    <Toggle 
                      checked={isEnabled}
                      onChange={(value) => onInputChange(`businessInteractions.${key}.enabled`, value)}
                    />
                  </div>
                  <h5 className="font-semibold text-gray-900 mb-2">{config.label}</h5>
                  <p className="text-sm text-gray-600 mb-4">{config.description}</p>
                  
                  {/* Feature indicators */}
                  <div className="flex flex-wrap gap-1">
                    {config.pricing && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Payments</span>}
                    {config.approval && <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">Approval</span>}
                    {config.internal && <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Internal</span>}
                  </div>

                  {/* Quick configuration for enabled interactions */}
                  {isEnabled && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      <FormField label="Access Level">
                        <SelectInput
                          value={formData.businessInteractions?.[key]?.accessLevel || 'public'}
                          onChange={(value) => onInputChange(`businessInteractions.${key}.accessLevel`, value)}
                          options={[
                            { value: 'public', label: 'Public' },
                            { value: 'members', label: 'Members Only' },
                            { value: 'internal', label: 'Internal Only' }
                          ]}
                        />
                      </FormField>

                      {config.pricing && (
                        <Toggle
                          checked={formData.businessInteractions?.[key]?.enablePayments}
                          onChange={(value) => onInputChange(`businessInteractions.${key}.enablePayments`, value)}
                          label="Enable Payments"
                          description="Accept payments for this interaction"
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Global Business Settings */}
          {enabledBusinessInteractions.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-6">
              <h5 className="font-semibold text-gray-900 mb-4">Global Business Settings</h5>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="Default Admin Email">
                  <TextInput
                    value={formData.businessAdminEmail || ''}
                    onChange={(value) => onInputChange('businessAdminEmail', value)}
                    placeholder="admin@organisation.com"
                    type="email"
                  />
                </FormField>

                <FormField label="Default Currency">
                  <SelectInput
                    value={formData.defaultCurrency || 'ZAR'}
                    onChange={(value) => onInputChange('defaultCurrency', value)}
                    options={[
                      { value: 'ZAR', label: 'ZAR (R)' },
                      { value: 'USD', label: 'USD ($)' },
                      { value: 'EUR', label: 'EUR (€)' }
                    ]}
                  />
                </FormField>
              </div>

              <div className="mt-4">
                <Toggle
                  checked={formData.requireTermsAcceptance || false}
                  onChange={(value) => onInputChange('requireTermsAcceptance', value)}
                  label="Require Terms & Conditions"
                  description="Users must accept terms before submitting business interactions"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* NOTIFICATIONS TAB - Enhanced existing functionality */}
      {activeInteractionTab === 'notifications' && (
        <div className="space-y-8">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-yellow-600" />
            <h4 className="text-lg font-semibold text-gray-900">Notification Settings</h4>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Email Notifications">
              <SelectInput
                value={formData.emailNotificationFreq}
                onChange={(value) => onInputChange('emailNotificationFreq', value)}
                options={notificationFrequencies}
              />
            </FormField>

            <FormField label="SMS Notifications">
              <SelectInput
                value={formData.smsNotificationFreq}
                onChange={(value) => onInputChange('smsNotificationFreq', value)}
                options={notificationFrequencies}
              />
            </FormField>
          </div>

          <div className="space-y-4">
            <h5 className="font-medium text-gray-900">Notification Types</h5>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Toggle
                checked={formData.notifyOnComments}
                onChange={(value) => onInputChange('notifyOnComments', value)}
                label="New Comments"
                description="Get notified about new comments"
              />

              <Toggle
                checked={formData.notifyOnBusinessInteractions}
                onChange={(value) => onInputChange('notifyOnBusinessInteractions', value)}
                label="Business Interactions"
                description="Get notified about applications, bookings, etc."
              />

              <Toggle
                checked={formData.notifyOnReviews}
                onChange={(value) => onInputChange('notifyOnReviews', value)}
                label="New Reviews"
                description="Get notified about new reviews"
              />

              <Toggle
                checked={formData.notifyOnPayments}
                onChange={(value) => onInputChange('notifyOnPayments', value)}
                label="Payment Transactions"
                description="Get notified about payments and transactions"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};